/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-continue */
import { forwardRef, useEffect, useMemo, useState, useRef } from "react";
import Draggable from "react-draggable";
import { Delete, Settings } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { useResizeDetector } from "react-resize-detector";

import Popup from "../../Popup.js";
import { useSnackbar } from "../../../utils/index.js";
import Form from "../../Form.js";
import { Tooltip } from "../../../tctt.js";


import { Grid, IconButton, Typography } from "@mui/material";
import dashboardItem from "./components/index.js";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		height: "100%",
		background: "white",
		padding: "5px",
		display: "flex",
		flexDirection: "column",
		borderRadius: "10px",
		fontSize: "16px",
		boxShadow: "5px 5px 12px -5px rgba(0,0,0,0.8)",
		"-webkit-box-shadow": "5px 5px 12px -5px rgba(0,0,0,0.8)",
		"-moz-box-shadow": "5px 5px 12px -5px rgba(0,0,0,0.8)",
	},
	header: {
		width: "100%",
		height: "25px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "white",
		background: theme.palette.primary.main,
		borderTopLeftRadius: "5px",
		borderTopRightRadius: "5px",
		position: "relative",
	},
	rightHeader: {
		height: "100%",
		position: "absolute",
		top: "0px",
		right: "0px",
		display: "flex",
		alignItems: "center",
	},
	leftHeader: {
		height: "100%",
		position: "absolute",
		top: "0px",
		left: "0px",
		display: "flex",
		alignItems: "center",
	},
	content: {
		width: "100%",
		minHeight: "calc(100% - 35px)",
		marginTop: "10px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		position: "relative",
	},
}));

const toTitleCase = (phrase) => (phrase
	.toLowerCase()
	.split(" ")
	.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
	.join(" ")
);

const calculateFontSize = ({
	width,
	height,
	text = null,
	minFontSize = 5,
	maxFontSize = 20,
}) => {
	const scaleFactor = 0.1;

	if (!width || !height) return 16;

	const size = Math.min(width, height) * scaleFactor;
	if (text) {
		const textLength = text.length;
		return Math.max(
			minFontSize,
			Math.min(
				maxFontSize,
				Math.min(height, (width / textLength)),
			),
		);
	}

	return Math.max(minFontSize, Math.min(size, maxFontSize));
};

const deepEqual = (a, b) => {
	if (a === b) return true;
	if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
		return false;
	}

	if (Object.keys(a).length !== Object.keys(b).length) {
		return false;
	}

	for (const key in a) {
		if (!Object.prototype.hasOwnProperty.call(b, key) || !deepEqual(a[key], b[key])) {
			return false;
		}
	}

	return true;
};

const Item = forwardRef(({
	mode = "view",
	id = "",
	name: propsName = "",
	initialName = "",
	position: propsPosition = { x: 0, y: 0 },
	dashboardPosition: propsDashboardPosition = { x: 1, y: 1 },
	size: propsSize = { width: 1, height: 1 },
	boundaries: propsBoundaries = {
		minWidth: 1,
		minHeight: 1,
		maxWidth: 12,
		maxHeight: 12,
	},
	icon = null,
	iconHover = null,
	variables: propsVariables = {},
	elementDimensions: propsElementDimensions = 30,
	mapWidth: propsMapWidth = 100,
	mapHeight: propsMapHeight = 80,
	resolution: propsResolution = 0.1,
	itemCat = "",
	tooltip = { header: "", body: "" },
	updateItem: propsUpdateItem = () => { },
	deleteItem: propsDeleteItem = () => { },
	checkNameExists: propsCheckNameExists = () => { },
	createNewItem: propsCreateNewItem = () => { },
	setDescriptionTitle: propsSetDescriptionTitle = () => { },
	setDescriptionBody: propsSetDescriptionBody = () => { },
	onDrag: propsOnDrag = () => { },
	onDragStop: propsOnDragStop = () => { },
	liveVariables: propsLiveVariables = {},
	// eslint-disable-next-line no-unused-vars
}, ref) => {
	const classes = useStyles();
	const [name, setName] = useState(propsName);
	const [position, setPosition] = useState(propsPosition);
	const [variables, setVariables] = useState(propsVariables);
	const [liveVariables, setLiveVariables] = useState(propsLiveVariables);
	// eslint-disable-next-line no-unused-vars
	const [elementDimensions, setElementDimensions] = useState(propsElementDimensions);

	const [mapWidth, setMapWidth] = useState(propsMapWidth);
	const [mapHeight, setMapHeight] = useState(propsMapHeight);
	// eslint-disable-next-line no-unused-vars
	const [resolution, setResolution] = useState(propsResolution);

	const [dragging, setDragging] = useState(false);
	const [popupOpen, setPopupOpen] = useState(false);
	const [infoTooltipOpen, setInfoTooltipOpen] = useState(false);
	const [tmpFormValues, setTmpFormValues] = useState(Object.fromEntries(
		[...Object.keys(variables).map((variable) => [variable, variables[variable].value]), ["itemName", name]],
	));

	const [itemWidth, setItemWidth] = useState(10);
	const [itemHeight, setItemHeight] = useState(10);

	const { error } = useSnackbar();

	const sendUpdate = (newValues) => {
		const tmpState = {
			id,
			itemName: name,
			x: propsPosition.x,
			y: propsPosition.y,
			dashboardX: propsDashboardPosition.x,
			dashboardY: propsDashboardPosition.y,
			width: propsSize.width,
			height: propsSize.height,
			minWidth: propsBoundaries.minWidth,
			minHeight: propsBoundaries.minHeight,
			maxWidth: propsBoundaries.maxWidth,
			maxHeight: propsBoundaries.maxHeight,
			variables,
			itemCat,
		};

		for (const [key, value] of Object.entries(newValues)) {
			tmpState[key] = value;
		}

		propsUpdateItem(itemCat, id, tmpState);
	};

	const onMenuStart = (event) => {
		event.preventDefault();
		setDragging(true);
	};

	const onMenuDrag = (event) => {
		event.stopPropagation();
		const { x, y } = event;
		propsOnDrag(x, y);
	};

	const onMenuStop = (event) => {
		event.preventDefault();
		propsOnDragStop();
		setDragging(false);

		const mapComponent = document.querySelector("#map");
		const { top, right, bottom, left } = mapComponent.getBoundingClientRect();
		const { x, y } = event;

		if (x >= left && x <= right && y >= top && y <= bottom) {
			const newX = ((x - left) / mapWidth) * 100;
			const newY = ((bottom - y) / mapHeight) * 100;
			propsCreateNewItem(itemCat, newX, newY);
		}
	};

	const deleteItem = () => {
		propsDeleteItem(itemCat, id);
	};

	const checkNameExists = (value) => {
		const { matchesPattern, found } = propsCheckNameExists(value, initialName, itemCat, id);

		if (matchesPattern) {
			error("Name cannot follow the template category_number");
		} else if (found) {
			error("Name already exists");
		}

		return matchesPattern || found;
	};

	const onSettingsSubmit = (values) => {
		const { itemName, ...newValues } = values;

		if (itemName !== name && checkNameExists(itemName)) {
			return;
		}

		const newVariables = { ...variables };
		for (const [key, value] of Object.entries(newValues)) {
			newVariables[key].value = value;
		}

		sendUpdate({ variables: newVariables, itemName: itemName.toLowerCase().split(" ").join("_") });
		setPopupOpen(false);
	};

	const closePopup = () => {
		setPopupOpen(false);
		setTmpFormValues(Object.fromEntries(
			[...Object.keys(variables).map((variable) => [variable, variables[variable].value]), ["itemName", name]],
		));
	};

	const onDashboardItemResize = ({ width, height }) => {
		setItemWidth(width);
		setItemHeight(height);
	};

	const { ref: resizeRef } = useResizeDetector({
		refreshMode: "debounce",
		refreshRate: 50,
		onResize: onDashboardItemResize,
	});

	const formContent = useMemo(() => {
		const content = [{
			customType: "input",
			id: "itemName",
			type: "text",
			label: "Name",
			placeholder: "Name",
			value: tmpFormValues.itemName,
			onBlur: (event) => {
				setTmpFormValues({ ...tmpFormValues, itemName: event.target.value });
			},
		}];

		for (const [variable, options] of Object.entries(variables)) {
			const {
				type,
				name: varName,
				placeholder,
				min,
				max,
				step,
				precision,
				format,
				options: selectItems,
				variables: optionVariables,
				needs,
				hide,
				affects,
			} = options;

			if (needs) {
				let needsMet = true;
				for (const neededKey of Object.keys(needs)) {
					const neededValue = needs[neededKey];
					if (tmpFormValues?.[neededKey] !== undefined) {
						let found = false;
						for (const val of neededValue) {
							if (tmpFormValues[neededKey] === val) {
								found = true;
								break;
							}
						}

						needsMet = found;
						if (!needsMet) {
							break;
						}
					}
				}

				if (!needsMet) {
					continue;
				}
			}

			if (hide) continue;

			switch (type) {
				case "text": {
					content.push({
						customType: "wideInput",
						id: variable,
						type: "text",
						label: varName,
						placeholder,
						value: tmpFormValues[variable],
						onBlur: (event) => {
							setTmpFormValues({ ...tmpFormValues, [variable]: event.target.value });
						},
					});

					break;
				}

				case "number": {
					content.push({
						customType: "number",
						id: variable,
						type: "number",
						label: varName,
						value: tmpFormValues[variable],
						min: (typeof min === "string" ? tmpFormValues?.[min] : min),
						max: (typeof max === "string" ? tmpFormValues?.[max] : max),
						step,
						precision,
						...(format ? {
							format: (num) => `${num}${format}`,
						} : {}),
						...(format ? {
							parse: (num) => num.replace(format, ""),
						} : {}),
					});

					break;
				}

				case "boolean": {
					content.push({
						customType: "checkbox",
						id: variable,
						label: varName,
						defaultValue: tmpFormValues[variable],
						color: "primary",
						onChange: (value) => {
							setTmpFormValues({
								...tmpFormValues,
								[variable]: value,
							});
						},
					});
					break;
				}

				case "switch": {
					content.push({
						customType: "switch",
						id: variable,
						label: varName,
						defaultValue: tmpFormValues[variable],
						onChange: (value) => {
							setTmpFormValues({
								...tmpFormValues,
								[variable]: value
							});
						},
					});
					break;
				}

				case "select": {
					content.push({
						customType: "dropdown",
						id: variable,
						label: varName,
						defaultValue: tmpFormValues[variable],
						items: typeof selectItems === "string"
							? tmpFormValues[selectItems].map((item) => ({ value: item, text: item }))
							: selectItems.map((item) => ({ value: item, text: item })),
						onChange: (value) => {
							setTmpFormValues({ ...tmpFormValues, [variable]: value });
						},
					});
					break;
				}

				case "tagInput": {
					content.push({
						customType: "tagInput",
						id: variable,
						label: varName,
						defaultValue: tmpFormValues[variable],
						onChange: (values) => {
							const formValues = { ...tmpFormValues };
							formValues[variable] = values;

							if (affects) {
								for (const affect of affects) {
									if (!values.includes(tmpFormValues[affect])) {
										formValues[affect] = values[0];
									}
								}
							}

							setTmpFormValues(formValues);
						},
					});

					break;
				}

				case "stateInput": {
					content.push({
						customType: "stateInput",
						id: variable,
						label: varName,
						placeholder: "No automation steps",
						value: tmpFormValues[variable],
						variables: Object.fromEntries(optionVariables.map((variab) => [variab, { ...variables[variab], tmpFormValues }])),
						onChange: (values) => {
							setTmpFormValues({ ...tmpFormValues, [variable]: values });
						},
					});

					break;
				}

				default:
				// Do nothing
			}
		}

		content.push({
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Save",
		});

		return content;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [name, variables, tmpFormValues]);

	useEffect(() => {
		sendUpdate({});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setMapWidth(propsMapWidth);
	}, [propsMapWidth]);

	useEffect(() => {
		setMapHeight(propsMapHeight);
	}, [propsMapHeight]);

	useEffect(() => {
		if (propsPosition.x !== position.x || propsPosition.y !== position.y) {
			setPosition(propsPosition);
		}
	}, [position.x, position.y, propsPosition]);

	useEffect(() => {
		setVariables(propsVariables);
		setTmpFormValues(Object.fromEntries(
			[...Object.keys(propsVariables).map((variable) => [variable, propsVariables[variable].value]), ["itemName", name]],
		));
	}, [name, propsVariables]);

	useEffect(() => {
		setName(propsName);
	}, [propsName]);

	useEffect(() => {
		setResolution(propsResolution);
	}, [propsResolution]);

	useEffect(() => {
		setElementDimensions(propsElementDimensions);
	}, [propsElementDimensions]);

	useEffect(() => {
		if (!deepEqual(propsLiveVariables, liveVariables)) {
			setLiveVariables(propsLiveVariables);
		}
	}, [liveVariables, propsLiveVariables]);

	const nodeRef = useRef(null)


	return (
		<>
			{(mode === "edit" || mode === "view")
				? (
					<div ref={resizeRef} className={classes.root}>
						<div className={classes.header}>
							<Typography style={{ cursor: "default", fontSize: `${calculateFontSize({ width: itemWidth, height: itemHeight, text: name })}px` }}>
								{name}
							</Typography>
							{mode === "edit" && (
								<div className={classes.rightHeader} onMouseDown={(event) => { event.stopPropagation(); }}>
									<Tooltip title="Settings">
										<IconButton
											aria-label="settings"
											color="white"
											size="small"
											onClick={() => { setPopupOpen(true); }}
										>
											<Settings fontSize="small" />
										</IconButton>
									</Tooltip>
								</div>
							)}
							{mode === "edit" && (
								<div className={classes.leftHeader} onMouseDown={(event) => { event.stopPropagation(); }}>
									<Tooltip title="Delete">
										<IconButton
											aria-label="delete"
											size="small"
											onClick={deleteItem}
										>
											<Delete fontSize="small" color="error" />
										</IconButton>
									</Tooltip>
								</div>
							)}
						</div>
						<div className={classes.content} onMouseDown={(event) => { event.stopPropagation(); }}>
							{dashboardItem(
								itemCat,
								{
									itemWidth,
									itemHeight,
									variables,
									calculateFontSize,
									mode,
									liveVariables,
								},
							)}
						</div>
					</div>
				)
				: (
					<Draggable
						key={id.toString()}
						nodeRef={nodeRef}
						allowAnyClick={false}
						zIndex={100}
						position={{ x: 0, y: 0 }}
						onStart={onMenuStart}
						onDrag={onMenuDrag}
						onStop={onMenuStop}
					>
						<div
							ref={nodeRef}
							id={id.toString()}
							style={{
								margin: "0px",
								padding: "0px",
								boxShadow: "none",
								background: "transparent",
								cursor: mode === "menu"
									? "move"
									: (mode === "view"
										? "help"
										: dragging ? "move" : "pointer"),
								opacity: (dragging && mode === "menu") ? 0 : 1,
								transform: "translate(-50%, -50%)",
							}}
						>
							<img
								id={`itemImg_${id}`}
								src={
									infoTooltipOpen
										? iconHover
										: icon
								}
								alt=""
								draggable={false}
								style={{
									maxWidth: "100%",
									maxHeight: "100%",
									zIndex: dragging ? 1000 : 1,
								}}
								onMouseOver={() => {
									setInfoTooltipOpen(true);
									propsSetDescriptionTitle(tooltip.header);
									propsSetDescriptionBody(tooltip.body);
								}}
								onMouseOut={() => {
									setInfoTooltipOpen(false);
									propsSetDescriptionTitle(null);
									propsSetDescriptionBody(null);
								}}
								onFocus={() => {
									setInfoTooltipOpen(true);
									propsSetDescriptionTitle(tooltip.header);
									propsSetDescriptionBody(tooltip.body);
								}}
								onBlur={() => {
									setInfoTooltipOpen(false);
									propsSetDescriptionTitle(null);
									propsSetDescriptionBody(null);
								}}
							/>
						</div>
					</Draggable>
				)}
			<Popup
				key={`popup_${id}`}
				width="500px"
				open={popupOpen}
				title={`${toTitleCase(itemCat)} ${id} Settings`}
				onClose={closePopup}
			>
				<Grid display="flex" flexDirection="column" justifyContent="center" alignItems="center" onMouseDown={(event) => { event.stopPropagation(); }}>
					<Form key={JSON.stringify(tmpFormValues)} content={formContent} onSubmit={onSettingsSubmit} />
				</Grid>
			</Popup>
		</>
	);
});

export default Item;
